import React, { FunctionComponent, useEffect, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { IReservationOffersClientsClientData, useApi } from 'components/providers/ApiProvider'
import { useMessage } from 'components/elements/Message'
import { TextField } from 'components/elements/TextField'
import Drawer from '@material-ui/core/Drawer'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { Box, Theme } from '@material-ui/core'
import { Close, Edit, RemoveCircleOutline, Search } from '@material-ui/icons'
import IconButton from '@material-ui/core/IconButton'
import { SubmitButton } from 'Elements/SubmitButton'
import ListSkeleton from 'components/elements/ListSkeleton'
import ListLoadingIndicator from 'components/elements/ListLoadingIndicator'
import ListNoRecords from 'components/elements/ListNoRecords'
import EditClientNotesDialog, { EditClientNotesDialogData } from 'components/views/reservation-offers/partials/EditClientNotesDialog'
import GrantAccessForm from 'components/views/reservation-offers/partials/GrantAccessForm'

export interface ClientsDrawerData {
  id: number
  modify: boolean
}

type Props = {
  data: ClientsDrawerData
}
type Functions = {}
type AllProps = Props & Functions

const useClasses = makeStyles((theme: Theme) => ({
  paper: {
    background: 'white',
    padding: '16px 24px',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '500px',
    },
  },
  filterButton: {
    minWidth: 'auto',
    '& .MuiButton-startIcon': {
      marginLeft: 0,
      marginRight: 0,
    },
  },
  revokeButtonCell: {
    padding: '0 16px 0 0',
  },
}))

const NotesTableCell = withStyles(theme => ({
  root: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
    maxWidth: 200,
  },
}))(TableCell)

const ClientsDrawer: FunctionComponent<AllProps> = ({ data }) => {
  if (!data) {
    return null
  }

  const classes = useClasses()
  const { t } = useTranslation()
  const api = useApi()
  const { showSuccess, showError } = useMessage()

  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState<{ client: string, notes: string }>({
    client: '',
    notes: '',
  })
  const [loading, setLoading] = useState(false)
  const [clients, setClients] = useState<IReservationOffersClientsClientData[]>(null)

  const [editClientNotesDialogData, setEditClientNotesDialogData] = useState<EditClientNotesDialogData>(null)

  const load = () => {
    setLoading(true)

    api.getReservationOffersClients({
      offer_id: data.id,
      client: filter.client || undefined,
      notes: filter.notes || undefined,
    })
      .then(data => {
        setLoading(false)
        setClients(data.clients)
      })
      .catch(() => {
        setLoading(false)

        showError(`${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)
      })
  }

  useEffect(() => {
    setOpen(true)
  }, [data])

  useEffect(() => {
    if (!open) {
      return
    }

    load()
  }, [filter])

  const handleEnter = () => {
    load()
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleExited = () => {
    formal.change('client', initialValues.client)
    formal.change('notes', initialValues.notes)
    formal.clearErrors()

    setFilter({
      client: '',
      notes: '',
    })
    setClients(null)
  }

  const initialValues = {
    client: '',
    notes: '',
  }

  const schema = yup.object().shape({
    client: yup.string().trim(),
    notes: yup.string().trim(),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      setFilter(schema.cast(values))
    },
  })

  const handleEditNotes = (client: IReservationOffersClientsClientData) => {
    setEditClientNotesDialogData({
      permission_id: client.permission_id,
      notes: client.notes,
    })
  }

  const handleRevoke = (client: IReservationOffersClientsClientData) => {
    if (!confirm(t(`confirms.revokeReservationOfferAccess`))) {
      return
    }

    api.revokeReservationOfferAccess({
      permission_id: client.permission_id,
    })
      .then(data => {
        if (!data || !data.status) {
          throw new Error
        }

        load()

        showSuccess(t('messages.reservationOfferAccessRevoked'))
      })
      .catch(error => {
        let message: string

        switch (error.code) {
          default:
            message = t('alerts.tryLater')
        }

        showError(`${t('alerts.couldNotRevokeReservationOfferAccess')} ${message}`)
      })
  }

  return <>
    <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ className: classes.paper }} SlideProps={{ onEnter: handleEnter, onExited: handleExited }}>
      <Box display="flex" justifyContent="flex-end" paddingBottom={2}>
        <IconButton size="small" onClick={handleClose}>
          <Close />
        </IconButton>
      </Box>
      {data.modify && <>
        <GrantAccessForm data={{ id: data.id }} onSuccess={() => load()} />
      </>}

      <form {...formal.getFormProps()}>
        <Grid container spacing={1} style={{ paddingBottom: 8 }}>
          <TextField
            GridProps={{ xs: true }}
            label={t('client')}
            size="small"
            {...formal.getFieldProps('client')}
          />
          <TextField
            GridProps={{ xs: true }}
            label={t('notes')}
            size="small"
            {...formal.getFieldProps('notes')}
          />
          <SubmitButton
            GridProps={{ xs: 'auto' }}
            buttonText=""
            startIcon={<Search />}
            fullWidth={false}
            disabled={loading}
            className={classes.filterButton}
          />
        </Grid>
      </form>

      {!clients && <ListSkeleton columns={2} />}

      {clients && loading && (
        <ListLoadingIndicator />
      )}

      {clients && !clients.length && (
        <ListNoRecords />
      )}

      {clients && Boolean(clients.length) && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('client')}</TableCell>
                <TableCell>{t('notes')}</TableCell>
                {data.modify && (<TableCell />)}
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client, key) => {
                return <TableRow key={key} hover>
                  <TableCell>
                    {client.name}
                  </TableCell>
                  <NotesTableCell>
                    {client.notes}
                    {data.modify && (
                      <IconButton
                        size="small"
                        color="secondary"
                        style={{ marginLeft: client.notes ? 4 : 0 }}
                        onClick={() => handleEditNotes(client)}
                      >
                        <Edit fontSize="inherit" />
                      </IconButton>
                    )}
                  </NotesTableCell>
                  {data.modify && (
                    <TableCell
                      align="right"
                      className={classes.revokeButtonCell}
                      onClick={() => handleRevoke(client)}
                    >
                      <IconButton size="small">
                        <RemoveCircleOutline />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Drawer>
    <EditClientNotesDialog data={editClientNotesDialogData} onSuccess={() => load()} />
  </>
}

export default ClientsDrawer
